import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  Upload,
  Download,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  FileSpreadsheet,
  Music2,
  Copy,
  Search,
  RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  matched: { label: "Matched", color: "text-emerald-400", icon: CheckCircle2 },
  existing: { label: "Existing", color: "text-blue-400", icon: CheckCircle2 },
  low_confidence: { label: "Low Confidence", color: "text-amber-400", icon: AlertTriangle },
  not_found: { label: "Not Found", color: "text-red-400", icon: XCircle },
  pending: { label: "Pending", color: "text-syncchain-text-muted", icon: Loader2 },
  skipped: { label: "Skipped", color: "text-syncchain-text-muted", icon: null },
  error: { label: "Error", color: "text-red-400", icon: XCircle },
};

const CatalogueMatching = () => {
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [polling, setPolling] = useState(false);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const pollStatus = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API}/catalogue-match/status/${id}`);
      setJob(res.data);
      if (res.data.status === "completed" || res.data.status === "error") {
        setPolling(false);
        if (pollRef.current) clearInterval(pollRef.current);
        if (res.data.status === "completed") {
          toast.success(`Matching complete: ${res.data.summary.matched} of ${res.data.total_tracks} matched`);
        }
      }
    } catch (err) {
      console.error("Poll error:", err);
    }
  }, []);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(ext)) {
      toast.error("Supported formats: .xlsx, .csv");
      return;
    }

    setUploading(true);
    setJob(null);
    setJobId(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/catalogue-match/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setJobId(res.data.job_id);
      setPolling(true);
      toast.success(`Uploaded ${file.name} — processing ${res.data.total_tracks} tracks`);

      // Start polling
      pollStatus(res.data.job_id);
      pollRef.current = setInterval(() => pollStatus(res.data.job_id), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleExport = async () => {
    if (!jobId) return;
    try {
      const res = await axios.get(`${API}/catalogue-match/export/${jobId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `catalogue_match_${jobId.slice(0, 8)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const entries = job?.entries || [];
  const summary = job?.summary || {};
  const isProcessing = job?.status === "processing" || job?.status === "pass2" || job?.status === "pass3_brave";
  const totalProgress = entries.length > 0
    ? Math.round(((summary.matched || 0) + (summary.low_confidence || 0) + (summary.not_found || 0) + (summary.existing || 0)) / entries.length * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="catalogue-matching-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-syncchain-text">Catalogue Matching</h1>
        <p className="text-syncchain-text-secondary mt-1">
          Upload a catalogue file to bulk-resolve ISRC codes from track titles, artists, and composers
        </p>
      </div>

      {/* Upload Section */}
      <Card className="bg-syncchain-paper border-white/10">
        <CardHeader>
          <CardTitle className="text-syncchain-text text-lg flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-syncchain-accent" />
            Upload Catalogue
          </CardTitle>
          <CardDescription className="text-syncchain-text-muted">
            Upload an .xlsx or .csv file with columns for Track Name, Artist, and/or Composer. Max 500 tracks.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <input type="file" accept=".xlsx,.xls,.csv" ref={fileInputRef} onChange={handleUpload} className="hidden" />

          <div
            onClick={() => !uploading && !isProcessing && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              uploading || isProcessing
                ? "border-white/5 opacity-50 cursor-not-allowed"
                : "border-white/10 hover:border-syncchain-accent/40 hover:bg-syncchain-accent/5"
            }`}
            data-testid="catalogue-match-dropzone"
          >
            {uploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="w-8 h-8 text-syncchain-accent animate-spin" />
                <p className="text-sm text-syncchain-text-secondary">Uploading and parsing...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="w-8 h-8 text-syncchain-text-muted" />
                <p className="text-sm text-syncchain-text-secondary">Click to upload .xlsx or .csv</p>
                <p className="text-xs text-syncchain-text-muted">Columns: Track Name, Artist (optional), Composer (optional)</p>
              </div>
            )}
          </div>

          {/* How it works */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs text-syncchain-text-secondary pt-2">
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">1.</span>
              <span>Cleans titles — extracts embedded composer names, fixes encoding</span>
            </div>
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">2.</span>
              <span>Pass 1: Matches tracks with artist names to ISRCs instantly</span>
            </div>
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">3.</span>
              <span>Pass 2: Uses composer data to resolve remaining tracks via scoring</span>
            </div>
            <div className="flex gap-2">
              <span className="text-syncchain-accent font-bold">4.</span>
              <span>Pass 3: Discovers performing artists via web search for unresolved tracks</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {job && (
        <>
          {/* Progress & Summary */}
          <Card className="bg-syncchain-paper border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <h3 className="font-heading font-semibold text-syncchain-text">
                    {job.filename}
                  </h3>
                  <Badge variant="outline" className={`${
                    job.status === "completed" ? "text-emerald-400 border-emerald-400/30" :
                    job.status === "error" ? "text-red-400 border-red-400/30" :
                    "text-syncchain-accent border-syncchain-accent/30"
                  }`}>
                    {isProcessing ? (
                      <><Loader2 className="w-3 h-3 mr-1 animate-spin" />{job.status === "pass3_brave" ? "Pass 3: Discovering artists..." : job.status === "pass2" ? "Pass 2: Composer matching..." : "Pass 1: Resolving..."}</>
                    ) : job.status === "completed" ? "Complete" : job.status}
                  </Badge>
                </div>
                {job.status === "completed" && (
                  <div className="flex gap-2">
                    <Button onClick={handleExport} variant="outline" className="border-white/10 text-syncchain-text-secondary" data-testid="catalogue-match-export-btn">
                      <Download className="w-4 h-4 mr-2" />Export All (CSV)
                    </Button>
                    <Button
                      onClick={async () => {
                        try {
                          const res = await axios.get(`${API}/catalogue-match/download-review/${jobId}`, { responseType: "blob" });
                          const url = URL.createObjectURL(res.data);
                          const a = document.createElement("a");
                          a.href = url;
                          a.download = `unmatched_review_${jobId.slice(0, 8)}.xlsx`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success("Unmatched review exported");
                        } catch (err) {
                          toast.error("Download failed");
                        }
                      }}
                      variant="outline"
                      className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
                      data-testid="catalogue-match-unmatched-btn"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />Unmatched Review (XLSX)
                    </Button>
                  </div>
                )}
              </div>

              {isProcessing && <Progress value={totalProgress} className="h-2 mb-4 [&>div]:bg-syncchain-accent" />}

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-emerald-400">{summary.matched || 0}</p>
                  <p className="text-xs text-syncchain-text-muted">Matched</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-amber-400">{summary.low_confidence || 0}</p>
                  <p className="text-xs text-syncchain-text-muted">Low Confidence</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-400">{summary.not_found || 0}</p>
                  <p className="text-xs text-syncchain-text-muted">Not Found</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-400">{summary.existing || 0}</p>
                  <p className="text-xs text-syncchain-text-muted">Existing</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-syncchain-text-muted">{summary.pending || 0}</p>
                  <p className="text-xs text-syncchain-text-muted">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results Table */}
          <Card className="bg-syncchain-paper border-white/10" data-testid="catalogue-match-results">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10 text-syncchain-text-muted text-xs uppercase tracking-wider">
                      <th className="py-3 px-4 text-left w-8">#</th>
                      <th className="py-3 px-4 text-left">Original Title</th>
                      <th className="py-3 px-4 text-left">Artist</th>
                      <th className="py-3 px-4 text-left">ISRC</th>
                      <th className="py-3 px-4 text-left">Matched As</th>
                      <th className="py-3 px-4 text-left w-16">Score</th>
                      <th className="py-3 px-4 text-left w-10">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((r, i) => {
                      const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                      const Icon = cfg.icon;
                      return (
                        <tr key={`${r.original_title}-${i}`} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors">
                          <td className="py-2.5 px-4 text-syncchain-text-muted text-xs">{i + 1}</td>
                          <td className="py-2.5 px-4">
                            <div className="flex items-center gap-2">
                              <Music2 className="w-3.5 h-3.5 text-syncchain-text-muted flex-shrink-0" />
                              <span className="text-syncchain-text text-xs font-medium truncate max-w-[220px]" title={r.original_title}>
                                {r.original_title}
                              </span>
                            </div>
                          </td>
                          <td className="py-2.5 px-4 text-syncchain-text-secondary text-xs truncate max-w-[150px]" title={r.original_artist || r.original_composer}>
                            {r.original_artist || <span className="text-syncchain-text-muted italic">{r.composers?.[0] || "—"}</span>}
                          </td>
                          <td className="py-2.5 px-4">
                            {r.isrc ? (
                              <button onClick={() => copyToClipboard(r.isrc)} className="font-mono text-xs text-syncchain-accent hover:text-syncchain-accent-hover flex items-center gap-1" title="Click to copy">
                                {r.isrc}<Copy className="w-3 h-3 opacity-50" />
                              </button>
                            ) : r.status === "pending" ? (
                              <Loader2 className="w-3 h-3 text-syncchain-text-muted animate-spin" />
                            ) : (
                              <span className="text-syncchain-text-muted text-xs">—</span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-xs text-syncchain-text-muted truncate max-w-[200px]" title={r.spotify_title && r.spotify_artist ? `${r.spotify_title} — ${r.spotify_artist}` : ""}>
                            {r.spotify_title ? (
                              <span>{r.spotify_title} <span className="text-syncchain-text-muted">— {r.spotify_artist}</span></span>
                            ) : "—"}
                          </td>
                          <td className="py-2.5 px-4">
                            {r.match_confidence != null ? (
                              <span className={`text-xs font-mono ${r.match_confidence >= 70 ? 'text-emerald-400' : r.match_confidence >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                                {r.match_confidence}%
                              </span>
                            ) : "—"}
                          </td>
                          <td className="py-2.5 px-4">
                            {Icon ? (
                              <Icon className={`w-4 h-4 ${cfg.color} ${r.status === 'pending' ? 'animate-spin' : ''}`} />
                            ) : (
                              <span className="text-xs text-syncchain-text-muted">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default CatalogueMatching;
